import { Stack } from "@mui/material";
import { FC, useState, useMemo } from "react";
import { MRSearchbar, MRPagination, MRDrawer } from "components/material";
import useMultiSelect from "hooks/useMultiSelect";
import { useTranslation } from "react-i18next";
import useBlocks from "hooks/services/blocks/useBlocks";
import { PAGE_SIZE } from "config/pagination";
import BlockBulkActionsControl from "components/TablesComponents/Block/BlockBulkActionsControl";
import BlockTable from "components/TablesComponents/Block/BlockTable";
import Page from "components/Layout/Page";
import { NoDataRow } from "components/TablesComponents/Tables/styles";
import { useDebounce } from "use-debounce";
import Loader from "components/Loader";
import { Inputs as BlockFilters } from "components/Block/BlockFilterForm/model";
import BlockFilterForm from "components/Block/BlockFilterForm";
import { removeEmptyEntries } from "utils/object";
import { toggleDrawer } from "utils/drawer";
import FilterButton from "components/FilterButton";

const ProcessedModels: FC = () => {
  const { selected, toggleSelection, toggleAll, resetSelection } =
    useMultiSelect();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [searchbarText, setSearchbarText] = useState<string>("");
  const [debouncedSearchbarText] = useDebounce(searchbarText, 500);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState<BlockFilters | null>(null);

  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState<string>("_id");

  const formattedFilters = useMemo(() => {
    //Remove non filled values
    if (!filters) return;
    return removeEmptyEntries(filters);
  }, [filters]);

  const {
    data: blocks,
    isLoading: isLoadingBlocks,
    error: blocksError,
  } = useBlocks({
    page,
    size: PAGE_SIZE,
    order,
    sort: orderBy,
    text: debouncedSearchbarText,
    ...(formattedFilters && formattedFilters),
  });

  const sortHandler = (orderBy: string) => {
    setOrder(order === "asc" ? "desc" : "asc");
    setOrderBy(orderBy);
  };

  if (blocksError) throw new Error("Cannot get blocks");

  return (
    <Page>
      <MRDrawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={toggleDrawer(false, setFilterDrawerOpen)}
      >
        <BlockFilterForm
          appliedFilters={filters}
          onClearFilters={() => setFilters(null)}
          onApplyFilters={(values) => {
            setFilters(values);
            setFilterDrawerOpen(false);
          }}
          onClose={() => setFilterDrawerOpen(false)}
        />
      </MRDrawer>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <h1>{t("Processed Models")}</h1>
        <FilterButton
          onClick={() => setFilterDrawerOpen(true)}
          activeFilters={
            formattedFilters ? Object.keys(formattedFilters).length : 0
          }
        />
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        width="100%"
        justifyContent={"end"}
      >
        <Stack direction="row" gap={3} alignItems="center">
          <MRSearchbar
            onChange={(event) => setSearchbarText(event.target.value)}
          />
          <BlockBulkActionsControl
            selectedIds={selected}
            resetSelection={resetSelection}
          />
        </Stack>
      </Stack>
      {isLoadingBlocks ? (
        <Loader />
      ) : (
        <>
          <BlockTable
            blocks={blocks?.data || []}
            selected={selected}
            toggleSelection={toggleSelection}
            toggleAll={toggleAll}
            order={order}
            orderBy={orderBy}
            sortHandler={sortHandler}
          />
          <Stack direction="row" justifyContent="center">
            {blocks?.data && blocks.data.length ? (
              <MRPagination
                count={
                  blocks
                    ? Math.ceil(blocks?.pagination[0]?.total / PAGE_SIZE) || 1
                    : 1
                }
                page={page}
                onChange={(e, page) => setPage(page)}
              />
            ) : (
              <NoDataRow>{t("No Data")}</NoDataRow>
            )}
          </Stack>
        </>
      )}
    </Page>
  );
};

export default ProcessedModels;
